import type { Restrictions } from '@repo-level/core-front';
import { ETimeframe } from 'components/modals/Withdraw/helpers';
import type { TimeframeLimitData } from 'components/modals/Withdraw/hooks/useWithdrawTimeframeLimit';
import type { TOptions } from 'i18next';
import type { CountryCode } from 'libphonenumber-js';
import { isValidPhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { Maybe } from 'monet';
import { always, cond, pipe, replace, T } from 'ramda';
import { Trans } from 'react-i18next';
import { getCurrencyName } from './currency';

export type ValidatorMessage = string | React.ReactNode | [string, TOptions];

export type Validator<T = any, VS = any> = (
  value: T | undefined,
  values: VS,
  length?: number
) => ValidatorMessage | void;

export function composeValidators<T>(
  ...validators: Validator<T>[]
): Validator<T> {
  return (...args) => {
    for (const validator of validators) {
      const error = validator(...args);

      if (error != null) return error;
    }
  };
}

export const requiredFactory: (msg: ValidatorMessage) => Validator<any> =
  (msg) => (value) =>
    value == null ? msg : undefined;

export const required = requiredFactory('validation:required');

export const requiredDefault = requiredFactory('validation:requiredDefault');

export const requiredAndNotZeroFactory: (
  msg: ValidatorMessage
) => Validator<any> = (msg) => (value) =>
  !value || value === '0' ? msg : undefined;

export const requiredAndNotZero = requiredAndNotZeroFactory(
  'validation:required'
);

export function regex<T extends string>(
  re: RegExp,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : re.test(value) ? undefined : msg;
}

export function lt<T extends string>(
  param: number,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : value.length < param ? undefined : msg;
}

export const lt256 = lt(256, ['validation:string.max', { val: 255 }]);

export function gt<T extends string>(
  param: number,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : value.length > param ? undefined : msg;
}

export const latinLettersRefCodeRe = /^[a-zA-Z0-9_]+$/;

export const maskRefCode = (value: string) =>
  value.slice(0, 20).replace(/[^a-zA-Z0-9_]/g, '');

const refCodeRe = /^[A-Za-z0-9_]{3,20}$/;
export const refCodeRegexValidator = regex(
  refCodeRe,
  'validation:referralCode'
);
export const refCodeValidator = composeValidators(
  required,
  refCodeRegexValidator
);

// eslint-disable-next-line
const passRe = /^.{8,30}$/;
export const passRegexValidator = regex(passRe, ' ');
export const passValidator = composeValidators(required, passRegexValidator);

// eslint-disable-next-line
export const emailRe =
  /^((([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#$%&'*+\-/=?^_`{|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
export const email = regex(emailRe, 'validation:email');

export const checkboxRequired: Validator<boolean> = (value) =>
  value ? undefined : 'validation:required';

// eslint-disable-next-line
export const dateRe =
  /^((0[1-9]|[12][0-9]|3[01])(\/)(0[13578]|1[02]))|((0[1-9]|[12][0-9])(\/)(02))|((0[1-9]|[12][0-9]|3[0])(\/)(0[469]|11))(\/)\d{4}$/;
export const dateRegexValidator = regex(dateRe, 'validation:invalid');

export const zipCodeAURe = /^(0[289][0-9]{2})|([1-9][0-9]{3})$/;
export const zipCodeAURegexValidator = regex(zipCodeAURe, 'validation:invalid');

export const adultValidator: Validator<string> = (dob) => {
  if (dob == null) return;

  const [day, month, year] = dob.split('/');

  const myDob = new Date(`${month}/${day}/${year}`);
  const today = new Date();
  const maxDob = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  return maxDob.getTime() > myDob.getTime() ? undefined : 'validation:age18';
};

export const numberPhoneValidator: Validator<string> = (
  value,
  country?: string
) =>
  value != null &&
  value.length !== 0 &&
  (country == null
    ? isValidPhoneNumber(value.toString())
    : isValidPhoneNumber(value.toString(), country as CountryCode))
    ? undefined
    : 'validation:invalid';

export const maskAmount = (value: string) => {
  const strValue = String(value || '');

  if (strValue === '.') return '0.';

  const sanitizedValue = strValue.replace(/[^\d.]/g, '');
  const [wholePart, decimalPart] = sanitizedValue.split('.');

  if (Number(wholePart) >= 1000) return wholePart;
  if (!decimalPart) return sanitizedValue;

  return `${wholePart}.${decimalPart.replace(/\D/g, '')}`;
};

const cardNumberRe = /^[0-9]{4}\s?[0-9]{4}\s?[0-9]{4}\s?[0-9]{4,6}$/;
export const cardNumber = regex(cardNumberRe, 'validation:invalid');

const cvvCardRe = /^[0-9]{3,4}$/i;
export const cvvCard = regex(cvvCardRe, 'validation:invalidCvv');

const cardExpiryDateRe = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/i;
export const cardExpiryDate = regex(cardExpiryDateRe, 'validation:invalid');

type AmountLimitMsg = (restrictions: Restrictions) => ValidatorMessage;

const amountLimitFactory =
  (msgMin: AmountLimitMsg, msgMax: AmountLimitMsg) =>
  (restrictions: Restrictions) => {
    const toNumber = (value: any) =>
      Maybe.fromEmpty(value)
        .map(pipe(replace(',', ''), (s) => Number(s)))
        .getOrElse(0);

    const below = (restrictions: Restrictions) => (value: number) =>
      restrictions.minAmountValue != null &&
      restrictions.minAmountValue > value;

    const above = (restrictions: Restrictions) => (value: number) =>
      restrictions.maxAmountValue != null &&
      restrictions.maxAmountValue < value;

    const checkLimits = (restrictions: Restrictions) =>
      cond<number, ValidatorMessage | undefined>([
        [below(restrictions), always(msgMin(restrictions))],
        [above(restrictions), always(msgMax(restrictions))],
        [T, always(undefined)],
      ]);

    return pipe(toNumber, checkLimits(restrictions));
  };

const amountLimitMsgMin: AmountLimitMsg = ({
  minAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.min',
  { amount: minAmountValue, currency: amountCurrencyCode },
];

const amountLimitMsgMax: AmountLimitMsg = ({
  maxAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.max',
  { amount: maxAmountValue, currency: amountCurrencyCode },
];

export const amountLimit = amountLimitFactory(
  amountLimitMsgMin,
  amountLimitMsgMax
);

export const validateTimeframeLimit = (limitData?: TimeframeLimitData) => {
  const toNumber = (value: string | undefined) =>
    Maybe.fromEmpty(value)
      .map(pipe(replace(',', ''), (s) => Number(s)))
      .getOrElse(0);

  const checkLimit = (value: number) => {
    if (!limitData) return;

    const timeframes = [
      ETimeframe.Daily,
      ETimeframe.Weekly,
      ETimeframe.Monthly,
    ];

    const exceeded = timeframes.find(
      (timeframe) => value > limitData.leftover[timeframe]
    );

    if (exceeded) {
      const leftoverKey = limitData.leftover[exceeded]
        ? 'leftover'
        : 'noLeftover';

      return (
        <Trans
          components={{
            l: (
              <a
                className="limit-link"
                href="/terms-and-conditions#withdrawal-policy"
              />
            ),
          }}
          i18nKey={`timeframeLimit.${leftoverKey}.${exceeded}`}
          ns="validation"
          values={{
            currency: getCurrencyName(limitData.currency),
            limit: limitData.limit[exceeded],
            leftover: limitData.leftover[exceeded],
            resetDate: limitData.leftoverResetDate[exceeded],
          }}
        />
      );
    }
  };

  return pipe(toNumber, checkLimit);
};

export const validateEmail = composeValidators(required, email);

export const validatePassword = composeValidators(required, passValidator);

const isLaterThanToday = (date: string) => {
  const inputDate = moment(date, 'MM/YY').startOf('month');
  const today = moment().startOf('day');

  return inputDate.isAfter(today, 'month');
};

export const cardExpiryDateValidator: Validator<string> = (value?: string) =>
  isLaterThanToday(value!) ? undefined : 'validation:expiryDate.invalid';

export const xRegex = /[×*]/g;
export const moreThanOneSpaceRegex = /\s+/g;

// ts-ignore allows 'u' flag for emoji support, safe at runtime despite ES5 target.
export const emojiRegex =
  // @ts-ignore
  /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2B50}\u{2B55}\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F236}\u{1F238}-\u{1F23A}\u{1F250}\u{1F251}\u{1F300}-\u{1F320}\u{1F32D}-\u{1F335}\u{1F337}-\u{1F37C}\u{1F37E}-\u{1F393}\u{1F3A0}-\u{1F3CA}\u{1F3CF}-\u{1F3D3}\u{1F3E0}-\u{1F3F0}\u{1F3F4}\u{1F3F8}-\u{1F3FF}\u{1F400}-\u{1F43E}\u{1F440}\u{1F442}-\u{1F4FC}\u{1F4FF}-\u{1F53D}\u{1F54B}-\u{1F54E}\u{1F550}-\u{1F567}\u{1F57A}\u{1F595}\u{1F596}\u{1F5A4}\u{1F5FB}-\u{1F64F}\u{1F680}-\u{1F6C5}\u{1F6CC}\u{1F6D0}-\u{1F6D2}\u{1F6D5}-\u{1F6D7}\u{1F6EB}\u{1F6EC}\u{1F6F4}-\u{1F6FC}\u{1F7E0}-\u{1F7EB}\u{1F90C}-\u{1F93A}\u{1F93C}-\u{1F945}\u{1F947}-\u{1F978}\u{1F97A}\u{1F97B}\u{1F97C}-\u{1F9A2}\u{1F9A5}-\u{1F9AA}\u{1F9AE}-\u{1F9CA}\u{1F9CD}-\u{1F9FF}\u{1FA70}-\u{1FA74}\u{1FA78}-\u{1FA7A}\u{1FA80}-\u{1FA86}\u{1FA90}-\u{1FAA8}\u{1FAB0}-\u{1FAB6}\u{1FAC0}-\u{1FAC2}\u{1FAD0}-\u{1FAD6}]/gu;
